import { Language } from "@faire/web-api/indigofair/languages/Language";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";

import { EventsThemeBadge } from "@faire/retailer/components/Events/shared/EventsThemeBadge";
import {
  IBrandTileConfigurationProp,
  withConfigurationConsumer,
} from "@faire/retailer/components/LeanBrandTileRenderer/__internal__/configurableBrandTileUtils";
import { ILeanBrandTile } from "@faire/retailer/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { ShowroomTileBadge } from "@faire/retailer/components/Showroom/ShowroomTileBadge";
import { TopShopTileBadge } from "@faire/retailer/components/TopShop/TopShopTileBadge";
import { getLocaleKey } from "@faire/retailer/serialized-data/getLocaleKey";

import { useTopShopVisibility } from "../../TopShop/useTopShopVisibility";

const TOOLTIP_LANGUAGES = new Set<keyof typeof Language>([
  Language.SPA,
  Language.FRA,
  Language.ITA,
]);

interface IProps extends IBrandTileConfigurationProp {
  tile: ILeanBrandTile;
}

const BrandTileBadgeBase: React.FC<IProps> = (props) => {
  const { brand } = props.tile;

  const { isTopShopContentVisibleForBrand } = useTopShopVisibility(
    brand?.is_top_shop
  );

  const renderNonEventBadge = () => {
    if (brand?.is_showroom) {
      if (props.configuration.disableShowroom) {
        return null;
      }

      return (
        <StyledShowroomTileBadge
          $showEventBadge={!!brand?.is_highlighted_brand_for_promotional_event}
          token={brand?.token}
          customEventName="brand-tile"
        />
      );
    }

    if (isTopShopContentVisibleForBrand) {
      if (props.configuration.disableTopShop) {
        return null;
      }
      return (
        <StyledTopShopTileBadge
          $showEventBadge={!!brand?.is_highlighted_brand_for_promotional_event}
        />
      );
    }

    return null;
  };

  const renderEventBadge = () => {
    if (!brand?.is_highlighted_brand_for_promotional_event) {
      return null;
    }

    if (props.configuration.disableEventBadge) {
      return null;
    }

    const currentLanguage = getLocaleKey()?.language;
    const showTooltip =
      currentLanguage && TOOLTIP_LANGUAGES.has(currentLanguage);
    return (
      <AbsoluteWrapper>
        <EventsThemeBadge expandable showToolTip={showTooltip} />
      </AbsoluteWrapper>
    );
  };

  return (
    <Wrapper>
      {renderNonEventBadge()}
      {renderEventBadge()}
    </Wrapper>
  );
};

export const BrandTileBadge = observer(
  withConfigurationConsumer(BrandTileBadgeBase)
);

const Wrapper = styled(Row)`
  position: absolute;
  top: ${getSpacing("1x")};
  left: ${getSpacing("1x")};
  z-index: 3;
`;

const AbsoluteWrapper = styled.div`
  position: absolute;
`;

const SharedEventBadgeShownStyle = css<{
  $showEventBadge: boolean;
}>`
  ${({ $showEventBadge }) =>
    $showEventBadge
      ? css`
          margin-top: ${getSpacing(2)};
          margin-left: ${getSpacing(44)};
        `
      : ""}
`;

const StyledTopShopTileBadge = styled(TopShopTileBadge)<{
  $showEventBadge: boolean;
}>`
  ${SharedEventBadgeShownStyle};
  ${AbsoluteWrapper}:hover & {
    opacity: 0;
  }
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  position: relative;
`;

const StyledShowroomTileBadge = styled(ShowroomTileBadge)<{
  $showEventBadge: boolean;
}>`
  ${SharedEventBadgeShownStyle};
  ${AbsoluteWrapper}:hover & {
    opacity: 0;
  }
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  position: relative;
`;
